<template>
  <VueFinalModal
    v-model="isOpen"
    content-transition="vfm-fade"
    :click-to-close="true"
    class="flex justify-center items-center p-6"
    content-class="relative w-full max-w-6xl h-[85vh] bg-[#fafbfc] rounded-[40px] shadow-2xl flex overflow-hidden z-50 ring-1 ring-black/5"
    @close="close"
  >
    <div class="flex w-full h-full min-h-0">
      <div class="w-full lg:w-[32%] bg-[#1c4848] relative flex flex-col p-8 text-white shrink-0 overflow-hidden">
        <div class="relative z-10 flex items-center gap-3 mb-6 opacity-80">
          <div class="w-8 h-8 rounded-full bg-white/10 border border-white/10 flex items-center justify-center text-[#ecd79f]">
            <IconSlidingPuzzle class="w-[18px] h-[18px]" />
          </div>
          <span class="text-xs font-bold tracking-[0.2em] uppercase">Preview</span>
        </div>
        <div class="relative z-10 flex-1 flex items-center justify-center">
          <div class="relative w-[260px] h-[520px] bg-white rounded-[40px] border-[8px] border-[#2d3748] shadow-2xl flex flex-col overflow-hidden">
            <div class="absolute top-0 inset-x-0 h-6 bg-[#2d3748] z-20 flex justify-center">
              <div class="w-20 h-4 bg-[#1a202c] rounded-b-xl"></div>
            </div>
            <div class="flex-1 bg-[#fafbfc] w-full pt-8 pb-4 px-4 flex flex-col items-center min-h-0">
              <h4 class="text-[#1c4848] font-serif font-bold mb-3 shrink-0 text-sm">Sliding Puzzle</h4>
              <div class="w-[200px] h-[200px] shrink-0 rounded-xl overflow-hidden relative shadow-lg bg-[#1c4848]">
                <img v-if="puzzlePreviewUrl" :src="puzzlePreviewUrl" class="w-full h-full object-cover opacity-60" alt="">
                <div
                  class="absolute inset-0 grid gap-0.5"
                  :style="{ gridTemplateColumns: `repeat(${slidingPreviewN}, 1fr)`, gridTemplateRows: `repeat(${slidingPreviewN}, 1fr)` }"
                >
                  <div
                    v-for="i in slidingPreviewTotal"
                    :key="i"
                    class="border border-white/20 aspect-square min-w-0 min-h-0"
                    :class="i === slidingPreviewTotal ? 'bg-[#fafbfc]/10 backdrop-blur-sm flex items-center justify-center text-[6px] uppercase font-bold text-white' : ''"
                  >
                    {{ i === slidingPreviewTotal ? 'Empty' : '' }}
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
        <div class="relative z-10 mt-auto pt-8 border-t border-white/10 text-white/30">
          <p class="text-[9px] font-bold uppercase tracking-widest">Slicing Mode</p>
          <p class="text-[10px] font-mono mt-0.5 text-[#ecd79f]">{{ form.grid_size || '3x3' }} ({{ slidingPreviewN }}×{{ slidingPreviewN }})</p>
        </div>
      </div>

      <div class="flex-1 flex flex-col bg-[#fafbfc] relative overflow-hidden min-w-0">
        <header class="px-8 py-6 border-b border-[#1c4848]/5 flex items-center justify-between bg-white/50 backdrop-blur-sm sticky top-0 z-30">
          <h2 class="text-xl font-bold text-[#1c4848] tracking-tight">Create Sliding Puzzle</h2>
          <div class="flex gap-3">
            <button type="button" class="px-4 py-2 rounded-xl text-xs font-bold text-[#1c4848]/60 hover:text-[#1c4848] hover:bg-[#1c4848]/5 transition-colors" @click="close">Cancel</button>
            <button type="button" :disabled="!canSave" class="px-5 py-2 rounded-xl text-xs font-bold text-white bg-[#1c4848] shadow-lg shadow-[#1c4848]/20 hover:bg-[#153838] active:scale-95 transition-all flex items-center gap-2 disabled:opacity-50 disabled:cursor-not-allowed" @click="save">
              <svg xmlns="http://www.w3.org/2000/svg" width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2"><path d="M19 21H5a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h11l5 5v11a2 2 0 0 1-2 2z"/><polyline points="17 21 17 13 7 13 7 21"/><polyline points="7 3 7 8 15 8"/></svg>
              Save Game
            </button>
          </div>
        </header>

        <div class="flex-1 overflow-y-auto custom-scrollbar p-8">
          <form class="space-y-8 max-w-3xl mx-auto" @submit.prevent="save">
            <section>
              <h4 class="text-xs font-bold text-[#1c4848] uppercase tracking-widest mb-4">General Information</h4>
              <div class="grid grid-cols-1 md:grid-cols-2 gap-5">
                <div class="md:col-span-2">
                  <TextInput v-model="form.name" label="Puzzle Title" placeholder="e.g. The Holy Kaaba" input-id="sliding-title" />
                </div>
                <div class="md:col-span-2">
                  <TextareaInput v-model="form.description" label="Description" placeholder="Instructions for the player..." :rows="3" textarea-id="sliding-desc" />
                </div>
                <div class="flex flex-col">
                  <label class="block text-[10px] font-bold text-[#1c4848]/60 uppercase tracking-wider mb-1.5 ml-1">Difficulty</label>
                  <DifficultyBadge :difficulty="suggestedDifficulty" />
                  <span class="text-[10px] text-[#1c4848]/40 mt-1">From grid size</span>
                </div>
                <div>
                  <label class="block text-[10px] font-bold text-[#1c4848]/60 uppercase tracking-wider mb-1.5 ml-1">Grid</label>
                  <select v-model="form.grid_size" class="w-full appearance-none bg-white border border-[#1c4848]/10 rounded-2xl px-4 py-3 text-sm font-medium text-[#1c4848] focus:outline-none focus:border-[#1c4848]/30 focus:ring-4 focus:ring-[#1c4848]/5 shadow-sm cursor-pointer">
                    <option value="3x3">3x3 (Standard)</option>
                    <option value="4x4">4x4 (Hard)</option>
                    <option value="5x5">5x5 (Expert)</option>
                  </select>
                </div>
                <div>
                  <SelectInput v-model="form.languageId" label="Language" select-id="sliding-language" :options="languageOptions" />
                </div>
              </div>
            </section>

            <section>
              <h4 class="text-xs font-bold text-[#1c4848] uppercase tracking-widest mb-4">Image upload</h4>
              <UploadImageSizeSelect v-model="form.uploadSize" select-id="create-sliding-upload-size" />
            </section>

            <section>
              <h4 class="text-xs font-bold text-[#1c4848] uppercase tracking-widest mb-4">Puzzle Image</h4>
              <div class="grid grid-cols-1 gap-5">
                <div class="w-full">
                  <label class="block text-[10px] font-bold text-[#1c4848]/60 uppercase tracking-wider mb-1.5 ml-1">Cover / Preview</label>
                  <div class="w-full h-40">
                    <ImageDropzone v-model="form.coverFile" input-id="sliding-cover" size="small" class="h-full w-full" />
                  </div>
                </div>
                <div class="w-full">
                  <label class="block text-[10px] font-bold text-[#1c4848]/60 uppercase tracking-wider mb-1.5 ml-1">Puzzle Image (1:1)</label>
                  <div class="w-full min-h-[140px]">
                    <ImageDropzone v-model="form.puzzleFile" input-id="sliding-puzzle" title-text="Click to upload puzzle image" hint-text="High resolution JPG or PNG (1:1)" class="h-full w-full min-h-[140px]" />
                  </div>
                </div>
              </div>
            </section>
          </form>
        </div>
      </div>
    </div>
  </VueFinalModal>
</template>

<script setup>
import { ref, computed, reactive, watch, onBeforeUnmount } from 'vue';
import { VueFinalModal } from 'vue-final-modal';
import TextInput from '@/components/forms/text/TextInput.vue';
import TextareaInput from '@/components/forms/textarea/TextareaInput.vue';
import SelectInput from '@/components/forms/select/SelectInput.vue';
import UploadImageSizeSelect from '@/components/forms/select/UploadImageSizeSelect.vue';
import ImageDropzone from '@/components/forms/dropzone/ImageDropzone.vue';
import DifficultyBadge from '@/components/app/dashboard/DifficultyBadge.vue';
import IconSlidingPuzzle from '@/components/app/dashboard/games/icons/IconSlidingPuzzle.vue';
import { useGamesStore } from '@/store/games/index.js';
import { useLanguagesStore } from '@/store/languages/index.js';
import { getLanguageLabel } from '@/utils/languageLabel.js';

const props = defineProps({ modelValue: { type: Boolean, default: false } });
const emit = defineEmits(['update:modelValue', 'close', 'saved']);

const isOpen = computed({
  get: () => props.modelValue,
  set: (v) => emit('update:modelValue', v),
});

const gamesStore = useGamesStore();
const languagesStore = useLanguagesStore();
const form = reactive({
  name: '',
  description: '',
  difficulty: 1,
  grid_size: '3x3',
  languageId: '',
  uploadSize: '',
  coverFile: null,
  puzzleFile: null,
});

const languageOptions = computed(() => {
  const list = languagesStore.languages ?? [];
  return [{ value: '', label: 'Select language' }, ...list.map((l) => ({ value: String(l.id), label: getLanguageLabel(l) }))];
});

const slidingPreviewN = computed(() => {
  const s = (form.grid_size || '3x3').toString();
  const n = parseInt(s.split('x')[0], 10);
  return n >= 3 && n <= 5 ? n : 3;
});
const slidingPreviewTotal = computed(() => slidingPreviewN.value * slidingPreviewN.value);

const suggestedDifficulty = computed(() => {
  const n = slidingPreviewN.value;
  return n <= 3 ? 1 : n <= 4 ? 2 : 3;
});

watch(() => props.modelValue, (v) => { if (v) languagesStore.fetchLanguages(); });

watch(() => form.grid_size, () => {
  form.difficulty = suggestedDifficulty.value;
}, { immediate: true });

const puzzlePreviewUrl = ref('');
watch(() => form.puzzleFile, (f) => {
  if (puzzlePreviewUrl.value) URL.revokeObjectURL(puzzlePreviewUrl.value);
  puzzlePreviewUrl.value = f instanceof File ? URL.createObjectURL(f) : '';
}, { immediate: true });
onBeforeUnmount(() => {
  if (puzzlePreviewUrl.value) URL.revokeObjectURL(puzzlePreviewUrl.value);
});

const canSave = computed(() => {
  return form.name?.trim() && form.languageId && form.coverFile instanceof File && form.puzzleFile instanceof File;
});

function close() {
  emit('update:modelValue', false);
  emit('close');
}

async function save() {
  if (!canSave.value) return;
  try {
    const payload = {
      name: form.name.trim(),
      description: form.description?.trim() || null,
      game_type: 'sliding_puzzle',
      difficulty: form.difficulty,
      language_id: form.languageId ? Number(form.languageId) : null,
      config: { grid_size: form.grid_size },
      rating: null,
    };
    const created = await gamesStore.create(payload);
    await gamesStore.uploadCover(created.id, form.coverFile, form.uploadSize);
    await gamesStore.uploadAssets(created.id, [form.puzzleFile], true, form.uploadSize);
    emit('saved');
    close();
  } catch (err) {
    console.error(err);
    alert('Failed to save. Please try again.');
  }
}
</script>

<style scoped>
.custom-scrollbar::-webkit-scrollbar { width: 6px; }
.custom-scrollbar::-webkit-scrollbar-track { background: transparent; }
.custom-scrollbar::-webkit-scrollbar-thumb { background: rgba(28, 72, 72, 0.2); border-radius: 3px; }
</style>
